/**
 * Shared trips (/trip/:slug) and shared calendars (/calendar/:slug) are made by
 * users at runtime, so the build has no page for them. Vercel served them its
 * 404 page, which carries the app, so browsers showed the page but crawlers got
 * HTTP 404 (ERI-2738). middleware.ts uses these helpers to look the slug up and,
 * when the trip or calendar exists, serve the app with a 200 and that page's
 * own title, description and canonical. A missing or private one still 404s.
 * Both are served from the shell prerendered for that kind of page, not the
 * homepage, so React hydrates markup from the same route (ERI-2783).
 *
 * No path aliases here: middleware.ts imports this file directly.
 */

const SITE = 'https://www.milkywayplanner.com';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export type SharedPath = { kind: 'trip' | 'calendar'; slug: string };

export interface SharedPageMeta {
  title: string;
  description: string;
  canonicalPath: string;
}

/** `/trip/<slug>` or `/calendar/<slug>` with one plain slug, or null. */
export function sharedPagePath(pathname: string): SharedPath | null {
  const match = /^\/(trip|calendar)\/([A-Za-z0-9-]{1,200})$/.exec(pathname);
  return match ? { kind: match[1] as SharedPath['kind'], slug: match[2] } : null;
}

/** "Mar 20" or "Mar 31, 2026" from a YYYY-MM-DD date, as the trip page prints it. */
function shortDate(iso: string, withYear: boolean): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}${withYear ? `, ${y}` : ''}`;
}

interface TripStop {
  stop_order: number;
  arrival_date: string;
  departure_date: string;
}

const TITLE_MAX = 60;
const DESCRIPTION_MAX = 155;

function fitTitle(candidates: string[]): string {
  for (const candidate of candidates) {
    if (candidate.length > 0 && candidate.length <= TITLE_MAX) return candidate;
  }
  const words = candidates[candidates.length - 1]?.split(/\s+/) ?? [];
  for (let end = words.length - 1; end >= 1; end--) {
    const candidate = words.slice(0, end).join(' ');
    if (candidate.length <= TITLE_MAX) return candidate;
  }
  return (candidates[0] ?? '').slice(0, TITLE_MAX);
}

function fitDescription(lead: string, extras: string[]): string {
  let text = lead;
  if (text.length > DESCRIPTION_MAX) {
    const words = text.replace(/[.\s]+$/g, '').split(/\s+/);
    while (words.length > 1 && words.join(' ').length > DESCRIPTION_MAX) words.pop();
    text = `${words.join(' ')}.`;
  }
  for (const extra of extras) {
    const next = `${text} ${extra}`;
    if (next.length <= DESCRIPTION_MAX) text = next;
  }
  return text;
}

/** Document title for a shared calendar. The place stays first; the suffix shrinks to stay within 60 characters. */
export function sharedCalendarTitle(locationName: string, year: number): string {
  return fitTitle([
    `${locationName} Milky Way Calendar ${year}`,
    `${locationName} Calendar ${year}`,
    `${locationName} ${year}`,
  ]);
}

/** Meta description for a shared calendar, from the place and year only. */
export function sharedCalendarDescription(locationName: string, year: number): string {
  return fitDescription(`Best Milky Way nights at ${locationName} in ${year}.`, [
    'Galactic core visibility, moon phases, and nightly ratings.',
    'Free shared calendar from Milky Way Planner.',
  ]);
}

/** Document title for a shared trip. Keeps "Milky Way Trip Itinerary" when it fits. */
export function sharedTripTitle(name: string): string {
  return fitTitle([
    `${name} - Milky Way Trip Itinerary`,
    `${name} - Milky Way Trip`,
    `${name} Trip`,
  ]);
}

/** Meta description for a shared trip, from the stop count and date range only. */
export function sharedTripDescription(stops: number, range: string): string {
  const count = `${stops} stop${stops === 1 ? '' : 's'}`;
  const lead = range ? `${count} · ${range}` : count;
  return fitDescription(`${lead} · Milky Way photography trip planned with Milky Way Planner.`, [
    'Galactic core timing and moon phase are listed for each stop.',
  ]);
}

/**
 * The title and description the trip or calendar page sets for itself once it
 * renders, or null when the page doesn't exist, is private, or the lookup fails.
 */
export async function lookupSharedPage(
  page: SharedPath,
  opts: { supabaseUrl: string; anonKey: string; fetchFn?: typeof fetch },
): Promise<SharedPageMeta | null> {
  const { supabaseUrl, anonKey, fetchFn = fetch } = opts;
  if (!supabaseUrl || !anonKey) return null;
  const rpc = page.kind === 'trip' ? 'get_shared_trip_by_slug' : 'get_shared_calendar_by_slug';
  const arg = page.kind === 'trip' ? { trip_slug: page.slug } : { calendar_slug: page.slug };
  try {
    const res = await fetchFn(`${supabaseUrl}/rest/v1/rpc/${rpc}`, {
      method: 'POST',
      headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(arg),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const canonicalPath = `/${page.kind}/${page.slug}`;

    if (page.kind === 'trip') {
      const name = data?.trip?.name;
      const stops: TripStop[] = Array.isArray(data?.stops) ? [...data.stops] : [];
      if (typeof name !== 'string') return null;
      stops.sort((a, b) => a.stop_order - b.stop_order);
      const range = stops.length
        ? `${shortDate(stops[0].arrival_date, false)} - ${shortDate(stops[stops.length - 1].departure_date, true)}`
        : '';
      return {
        title: sharedTripTitle(name),
        description: sharedTripDescription(stops.length, range),
        canonicalPath,
      };
    }

    const calendar = Array.isArray(data) ? data[0] : null;
    if (!calendar || typeof calendar.location_name !== 'string') return null;
    return {
      title: sharedCalendarTitle(calendar.location_name, calendar.year),
      description: sharedCalendarDescription(calendar.location_name, calendar.year),
      canonicalPath,
    };
  } catch {
    return null;
  }
}

/**
 * The page the build prerenders for each kind (App.tsx): the trip or calendar
 * route in its loading state, under this slug. The middleware serves it for
 * every shared page, so React hydrates markup from the same route (ERI-2783).
 * The underscores keep it outside sharedPagePath, so a request for the shell
 * itself goes straight to the static file, which carries a noindex.
 */
export const SHARED_PAGE_SHELL_SLUG = '__shell';

export function sharedPageShellPath(kind: SharedPath['kind']): string {
  return `/${kind}/${SHARED_PAGE_SHELL_SLUG}`;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const TITLE = /<title\b[^>]*>[\s\S]*?<\/title>/gi;
const CANONICAL = /<link\b[^>]*\srel="canonical"[^>]*>/gi;
const STRUCTURED_DATA = /<script\b[^>]*\stype="application\/ld\+json"[^>]*>[\s\S]*?<\/script>/gi;
const metaTag = (attr: 'name' | 'property', key: string) => new RegExp(`<meta\\b[^>]*\\s${attr}="${key}"[^>]*>`, 'gi');

/** Runs `edit` on the head only, so markup in the body (an SVG <title>) is never touched. */
function editHead(html: string, edit: (head: string) => string): string {
  const end = html.search(/<\/head>/i);
  return end < 0 ? html : edit(html.slice(0, end)) + html.slice(end);
}

/** `tag` in place of the first match, with any other matches dropped, or added at the end of the head. */
function setTag(head: string, pattern: RegExp, tag: string): string {
  let placed = false;
  const out = head.replace(pattern, () => {
    if (placed) return '';
    placed = true;
    return tag;
  });
  return placed ? out : out + tag;
}

const setMeta = (head: string, attr: 'name' | 'property', key: string, value: string) =>
  setTag(head, metaTag(attr, key), `<meta data-rh="true" ${attr}="${key}" content="${value}">`);

/**
 * The prerendered shell with this page's title, description, Open Graph and
 * Twitter text, and canonical, replacing the shell's own tags or adding them
 * where the shell has none, one of each. The shell's noindex becomes
 * "index, follow", and any structured data goes. Every value is escaped: trip
 * names are user text. The tags carry data-rh, as the prerendered ones do, so
 * the page's own SEOHead replaces them once it renders.
 */
export function withSharedHead(html: string, meta: SharedPageMeta): string {
  const title = escapeHtml(meta.title);
  const description = escapeHtml(meta.description);
  const url = escapeHtml(`${SITE}${meta.canonicalPath}`);

  return editHead(html, (head) => {
    let out = setTag(head, TITLE, `<title data-rh="true">${title}</title>`);
    out = setMeta(out, 'name', 'description', description);
    out = setMeta(out, 'name', 'robots', 'index, follow');
    out = setMeta(out, 'property', 'og:title', title);
    out = setMeta(out, 'property', 'og:description', description);
    out = setMeta(out, 'property', 'og:url', url);
    out = setMeta(out, 'name', 'twitter:title', title);
    out = setMeta(out, 'name', 'twitter:description', description);
    out = setTag(out, CANONICAL, `<link data-rh="true" rel="canonical" href="${url}">`);
    return out.replace(STRUCTURED_DATA, '');
  });
}

/**
 * The prerendered shell for a missing or private page, served with a 404: it
 * keeps exactly one noindex, and drops the canonical and og:url, which name
 * the shell's own path.
 */
export function withNotFoundHead(html: string): string {
  return editHead(html, (head) =>
    setMeta(head, 'name', 'robots', 'noindex, nofollow').replace(CANONICAL, '').replace(metaTag('property', 'og:url'), ''),
  );
}
